import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, Clock, MapPin, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { SectionHeading, Overline } from "@/components/Primitives";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CONTACT } from "@/data/site";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error("Please fill in name, email, and message."); return; }
    setBusy(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent! We'll get back to you shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send message.");
    } finally { setBusy(false); }
  };

  const CARDS = [
    { icon: Phone, label: "Call Us", value: CONTACT.phone, href: `tel:${CONTACT.phoneRaw}` },
    { icon: Phone, label: "Alternate Line", value: CONTACT.phoneAlt, href: `tel:${CONTACT.phoneAltRaw}` },
    { icon: Mail, label: "Email Us", value: CONTACT.email, href: `mailto:${CONTACT.email}` },
    { icon: Clock, label: "Business Hours", value: CONTACT.hours },
    { icon: MapPin, label: "Visit Us", value: CONTACT.address },
  ];

  return (
    <div>
      <section className="pt-36 pb-16 bg-brand-green">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading light overline="Contact" title="Let's solve your pest problem"
            subtitle="Reach out any time — our team responds fast, and emergencies get same-day priority." />
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2 space-y-4">
            {CARDS.map((c) => (
              <div key={c.label} className="bg-white rounded-2xl p-6 border border-black/5 shadow-sm flex items-start gap-4">
                <div className="h-11 w-11 rounded-xl bg-brand-green/5 flex items-center justify-center shrink-0"><c.icon className="h-5 w-5 text-brand-green" /></div>
                <div><p className="text-xs uppercase tracking-wider font-semibold text-brand-muted">{c.label}</p>
                  {c.href ? <a href={c.href} className="font-semibold text-brand-green hover:text-brand-gold transition-colors">{c.value}</a> : <p className="font-medium text-brand-charcoal text-sm mt-1">{c.value}</p>}</div>
              </div>
            ))}
            <a href={`tel:${CONTACT.phoneRaw}`} data-testid="contact-emergency-btn" className="block">
              <div className="bg-[#E53E3E] rounded-2xl p-6 text-white flex items-center gap-4 hover:bg-[#c53030] transition-colors">
                <Phone className="h-6 w-6" /><div><p className="font-bold">Emergency Line</p><p className="text-white/80 text-sm">24/7 urgent response — call now</p></div>
              </div>
            </a>
          </div>

          <div className="lg:col-span-3">
            <div className="bg-white rounded-3xl p-8 border border-black/5 shadow-sm">
              <Overline>Send a Message</Overline>
              <h3 className="font-heading text-2xl font-bold text-brand-green mt-2">We'd love to hear from you</h3>
              <form onSubmit={submit} className="mt-6 space-y-5">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label className="text-brand-charcoal">Full Name</Label><Input data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 h-12 rounded-xl" placeholder="Jane Doe" /></div>
                  <div><Label className="text-brand-charcoal">Phone</Label><Input data-testid="contact-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-2 h-12 rounded-xl" placeholder="(555) 123-4567" /></div>
                </div>
                <div><Label className="text-brand-charcoal">Email</Label><Input data-testid="contact-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 h-12 rounded-xl" placeholder="jane@email.com" /></div>
                <div><Label className="text-brand-charcoal">Message</Label><Textarea data-testid="contact-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-2 rounded-xl min-h-[130px]" placeholder="Tell us about your pest problem..." /></div>
                <Button data-testid="contact-submit-btn" type="submit" disabled={busy} className="w-full h-13 rounded-full bg-brand-green text-white font-bold hover:bg-brand-greenHover h-12">
                  {busy ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Sending...</> : <><Send className="h-5 w-5 mr-2" /> Send Message</>}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-[2rem] overflow-hidden border border-black/5 shadow-lg h-[400px]">
            <iframe title="EXORA Location" src="https://www.google.com/maps?q=Surrey,British+Columbia,Canada&output=embed" className="w-full h-full border-0" loading="lazy" />
          </div>
        </div>
      </section>
    </div>
  );
}
